import { useEffect, useMemo, useState } from "react";
import { TaskListQuery } from "@common/contracts/task.contract";
import { FiltersTypes } from "@common/types/filters/general";
import type { TaskListFiltersCache } from "@hooks/view/useTasksListFiltersCache";
import { buildTaskListQuery } from "./buildTaskListQuery";
import { taskListQueryToOverlayFilters } from "./taskListOverlayFilters";

const keepFilters = (filters: TaskListQuery) => filters;

interface UseCachedTaskListFiltersParams {
    cache: TaskListFiltersCache;
    defaultQuery: TaskListQuery;
    resolveFilters?: (filters: TaskListQuery) => TaskListQuery;
    initialOverlayFilters?: FiltersTypes;
}

export function useCachedTaskListFilters({
    cache: [cachedFilters, setCachedFilters],
    defaultQuery,
    resolveFilters = keepFilters,
    initialOverlayFilters,
}: UseCachedTaskListFiltersParams) {
    const [overlayFilters, setOverlayFilters] = useState<FiltersTypes>(
        () =>
            initialOverlayFilters ??
            taskListQueryToOverlayFilters(cachedFilters),
    );
    const [filters, setFilters] = useState<TaskListQuery>(() => ({
        ...defaultQuery,
        ...cachedFilters,
    }));

    const tasksQuery = useMemo(
        () =>
            buildTaskListQuery({
                filters: resolveFilters(filters),
                overlayFilters,
            }),
        [filters, overlayFilters, resolveFilters],
    );

    useEffect(() => {
        setCachedFilters(tasksQuery);
    }, [setCachedFilters, tasksQuery]);

    return { tasksQuery, setFilters, overlayFilters, setOverlayFilters };
}
